import { Component, effect, ElementRef, inject, signal, Output, EventEmitter } from '@angular/core';
import { FormField, required, form } from '@angular/forms/signals';
import { ProductService } from '../../../../services/product/product.service';
import { AddImageComponent } from '../../../../atoms/add-image/add-image.component';
import { S3BucketEnum } from '../../../../common/general-types/media-assets.dto';
import { CurrencyPipe } from '@angular/common';
import { CreateBriefService } from '../../../briefs/service/create-brief/create-brief.service';
import { SlickOnboard } from '../../service/slick-onboard/slick-onboard.service';
import { GptCompletionMessage } from '../../../../common/general-types/open-ai.dto';
import { ChatGptService } from '../../../../services/chat-gpt/chat-gpt.service';
import { CreateProductDto, UgcProductResponseDto } from '../../../../common/general-types/products.dto';
import { CurrentUserService } from '../../../../common/current-user/current-user.service';
import { ImageService } from '../../../../services/image/image.service';
import { firstValueFrom } from 'rxjs';
import { FirebaseService } from '../../../../common/firebase/firebase.service';
import { NgxConnectionBeamComponent } from '@omnedia/ngx-connection-beam';
import { LoadingInlineComponent } from '../../../../atoms/loading-inline/loading-inline.component';
import { BriefAudienceType, BriefResponseDto, BriefStatusEnum, CreateBriefDto, DeliveryTypeEnum, InfluencerTypeEnum, SubmittedPostVersionEnum } from '../../../../common/general-types/briefs.dto';
import { PricingModelV2Service } from '../../../../services/pricing-model-v2/pricing-model-v2.service';
import { VideoFormatEnum } from '../../../../common/general-types/briefs.dto';
import { BasePricingModel } from '../../../../common/general-types/pricing.dto';
import { CentralManagementService } from '../../../../services/central-management/central-management.service';
import { GaService } from '@/app/services/ga/ga';

@Component({
  selector: 'v2-speedy-setup',
  standalone: true,
  imports: [FormField, AddImageComponent, CurrencyPipe, NgxConnectionBeamComponent],
  templateUrl: './speedy-setup.html',
  styleUrl: './speedy-setup.css',
})
export class SpeedySetupComponent {

  @Output() briefCreated = new EventEmitter<BriefResponseDto>();
  

  VideoFormatEnum = VideoFormatEnum;
  DeliveryTypeEnum = DeliveryTypeEnum;
  InfluencerTypeEnum = InfluencerTypeEnum;
  BriefStatusEnum = BriefStatusEnum;
  BriefAudienceType = BriefAudienceType;

  S3BucketEnum = S3BucketEnum;
  spedySteps: { id: number, name: string, completed: boolean }[] = [
    { id: 1, name: 'Promoting', completed: false },
  ]

  currentStep = signal<number>(1);

  socialChannelOptions: {
    channelName: string;
    shortName: string;
    channelId: number;
    channelIcon: string;
  }[] = [
      {
        channelName: 'UGC Only',
        shortName: 'UGC',
        channelId: -1,
        channelIcon: '../../../../../assets/social-icon/ugc-videos.svg',
      },
      {
        channelName: 'TikTok Post',
        shortName: 'Post',
        channelId: 0,
        channelIcon: '../../../../../assets/social-icon/tik-tok-transparent.svg',
      },
      {
        channelName: 'Instagram Reel',
        shortName: 'Reel',
        channelId: 1,
        channelIcon: '../../../../../assets/social-icon/ig_reels_logo.png',
      },
      {
        channelName: 'Instagram Story',
        shortName: 'Story',
        channelId: 2,
        channelIcon: '../../../../../assets/social-icon/instagram-logo.png',
      },
      {
        channelName: 'YouTube Shorts',
        shortName: 'Short',
        channelId: 3,
        channelIcon: '../../../../../assets/social-icon/youtube_shorts.webp',
      },
      {
        channelName: 'Snapchat Spotlight',
        shortName: 'Spotlight',
        channelId: 4,
        channelIcon: '../../../../../assets/social-icon/snapchat-logo.svg',
      },
      {
        channelName: 'Snapchat Story',
        shortName: 'Story',
        channelId: 5,
        channelIcon: '../../../../../assets/social-icon/snapchat_story.jpg',
      },
    ]

  /** 
   * Step 1.
   * Promoting
   * 
   * Start
   */


  /** True while the backend product-details lookup is in flight. */
  findingProductDetails = signal<boolean>(false);

  /** When true, skip link-based lookup and collect product fields directly. */
  manualProductEntry = signal<boolean>(false);
  speedySetupFormMessage = signal<string | null>(null);

  /**
   * Top-level promotion choice:
   * - 'myBrand'  → promote the whole brand using the default product created at signup.
   * - 'specific' → collect a specific product via the link/manual form.
   */
  productChoice = signal<'myBrand' | 'specific'>('myBrand');
  /** The brand's default product (productType 4), loaded/created for the "My Brand" choice. */
  brandProduct = signal<UgcProductResponseDto | undefined>(undefined);
  /** True while the brand's default product is being fetched or created. */
  loadingBrandProduct = signal<boolean>(false);

  /** 
   * Step 1.
   * 
   * End
   */



  /** 
   * Step 2.
   * Building the Brief
   * 
   * Start
   */

  quickPromptOptions: string[] = [
    'Create a simple starter brief considering the product and the campaign.',
    'Instruct the creator to showcase the product and build trust.',
    'Instruct the creator to give a testimonial from a happy customer.',
    'Instruct the creator to show how to use the product',
    'Instruct the creator to review the product and give an honest opinion.',
    'Instruct the creator to show how the product compares to other products.',
    'Instruct the creator to unbox the product and show what is inside.'
  ]
  quickStartIndex: number = 0;

  queryRunning = signal<boolean>(false);

  // Typewriter chat simulation
  chatMessages = signal<{ text: string; displayedText: string; isComplete: boolean }[]>([]);
  currentMessageIndex = signal<number>(0);
  currentCharIndex = signal<number>(0);
  private typewriterInterval: ReturnType<typeof setInterval> | null = null;
  private messageDelay: ReturnType<typeof setTimeout> | null = null;

  selectedPricingModel = signal<BasePricingModel | undefined>(undefined);
  savingBrief = signal<boolean>(false);

  private static readonly BRIEF_GEN_ERROR =
    'We could not generate a usable brief. Please try Generate Brief again.';

  private static readonly BRIEF_SCHEMA_SYSTEM: GptCompletionMessage = {
    role: 'system',
    content: `You are a social media expert helping a brand create a brief for content creators.
You MUST reply with ONLY one valid RFC8259 JSON object and nothing else.
The JSON object MUST have exactly these top-level keys and no others: "briefText", "dos", "donts", "creativeConcepts".
Do NOT use keys such as campaign_details, product_details, content_guidelines, deliverables, name, goal, or any other top-level shape.
- briefText: HTML string (the creator-facing brief)
- dos: HTML string (things the creator should do)
- donts: HTML string (things the creator should avoid)
- creativeConcepts: array of exactly 3 objects, each with numeric "id", string "name", and string "description" (plain text, not HTML)
Do not wrap the JSON in markdown code fences. Do not include commentary before or after the JSON.`,
  };

  /** 
   * Step 2.
   * 
   * End
   */


  /** Services */
  private productService = inject(ProductService);
  public createBriefService = inject(CreateBriefService);
  public slickOnboardService = inject(SlickOnboard);
  private chatGptService = inject(ChatGptService);
  private currentUserService = inject(CurrentUserService);
  private firebaseService = inject(FirebaseService);
  private pmv2Service = inject(PricingModelV2Service);
  private centralManagementService = inject(CentralManagementService);
  public imageService = inject(ImageService);
  private host = inject(ElementRef<HTMLElement>);
  private gaService = inject(GaService);
  /** Services - END */

  /** The currently focussed brand/business account, exposed for the template. */
  get focussedBrand() {
    return this.currentUserService.focussedBrand;
  }

  constructor() {
    effect(() => {
      if (this.manualProductEntry()) {
        return;
      }
      // Only depend on productLink so this effect runs iff productLink changes (not briefTitle or other fields)
      const rawLink = this.slickOnboardService.promotingForm.productLink().value() ?? '';
      const productLink = rawLink.trim();
      console.log('SpeedySetup -> productLink changed: ', productLink);

      if (productLink.length === 0) {
        return;
      }

      this.slickOnboardService.productDetailsModel.set({
        productDescription: '',
        productImage: '',
        productName: '',
        productPrice: '',
        currency: '',
        productCategory: '',
        productIndustry: '',
      });

      this.findProductDetails(productLink);
    });

    // "My Brand" is preselected, so load the default brand product up front.
    this.loadBrandProduct();
  }

  /**
   * Load the brand's default product (productType 4, created at signup) for the "My Brand"
   * choice. If none exists (e.g. a legacy account), create one from the brand's data so the
   * "My Brand" option always works.
   */
  async loadBrandProduct(): Promise<void> {
    const brandId = this.currentUserService.focussedBrand?.id;
    if (!brandId) {
      return;
    }

    this.loadingBrandProduct.set(true);
    try {
      const products = (await firstValueFrom(
        this.productService.getbusinessProducts(brandId),
      )) as UgcProductResponseDto[];

      let brandProduct =
        products?.find((p) => p.productType === 4) ??
        products?.find((p) => p.productCategory === 'Brand');

      if (!brandProduct) {
        brandProduct = await this.createDefaultBrandProduct();
      }

      this.brandProduct.set(brandProduct);
    } catch (err) {
      console.error('SpeedySetup -> loadBrandProduct error: ', err);
      try {
        this.brandProduct.set(await this.createDefaultBrandProduct());
      } catch (createErr) {
        console.error('SpeedySetup -> createDefaultBrandProduct fallback error: ', createErr);
      }
    } finally {
      this.loadingBrandProduct.set(false);
    }
  }

  /** Build and persist a default brand product mirroring the backend signup default. */
  private async createDefaultBrandProduct(): Promise<UgcProductResponseDto> {
    const brand = this.currentUserService.focussedBrand;
    const companyName = brand?.companyName ?? 'My brand';
    const newProduct: CreateProductDto = {
      productType: 4,
      productImage: brand?.companyLogo ?? '',
      productName: companyName,
      productDescription: `${companyName} is a brand that sells ${companyName} products.`,
      productValue: 0,
      currency: (brand?.currencyCode ?? '').toUpperCase(),
      website: brand?.website ?? '',
      accessInstructions: 'Visit the brand website to learn more about the brand.',
      creatorId: this.firebaseService.userId!,
      businessAccountId: brand.id,
      productCategory: 'Brand',
      productIndustry: 'Brand',
    };
    return firstValueFrom(this.productService.createUserProduct(newProduct));
  }

  /** Choose to promote the whole brand (default product). */
  selectMyBrand(): void {
    this.productChoice.set('myBrand');
    this.speedySetupFormMessage.set(null);
    if (!this.brandProduct() && !this.loadingBrandProduct()) {
      this.loadBrandProduct();
    }
  }

  /** Choose to promote a specific product via the link/manual form. */
  selectSpecificProduct(): void {
    this.productChoice.set('specific');
    this.speedySetupFormMessage.set(null);
  }

  /**
   * Look up product details for a pasted URL via the backend layered extractor.
   * Maps every returned field into the shared model. If the URL is invalid, the lookup
   * fails, or no product name could be resolved, we fall back to the manual-entry form
   * pre-filled with whatever partial data was found.
   */
  findProductDetails(productLink: string) {
    if (!productLink || productLink.length === 0) {
      return;
    }

    if (!this.isValidUrl(productLink)) {
      this.fallbackToManualEntry();
      return;
    }

    this.findingProductDetails.set(true);
    this.productService.getProductDetails(productLink, 'Clara').subscribe({
      next: (response: any) => {
        console.log('SpeedySetup -> getProductDetails response: ', response);
        this.findingProductDetails.set(false);

        if (!response || !response.productName) {
          // Nothing usable came back — seed the manual form with any partial data.
          if (response) {
            this.slickOnboardService.productDetailsModel.set({
              ...this.slickOnboardService.productDetailsModel(),
              productDescription: response.productDescription || '',
              productName: response.productName || '',
              productPrice: response.productPrice ? String(response.productPrice) : '',
              currency: response.currency || '',
              productImage: response.productImage || '',
              productCategory: response.productCategory || '',
              productIndustry: response.productIndustry || '',
            });
          }
          this.fallbackToManualEntry();
          return;
        }

        this.slickOnboardService.productDetailsModel.set({
          ...this.slickOnboardService.productDetailsModel(),
          productDescription: response.productDescription || '',
          productName: response.productName || '',
          productPrice: response.productPrice ? String(response.productPrice) : '',
          currency: response.currency || '',
          productImage: response.productImage || '',
          productCategory: response.productCategory || '',
          productIndustry: response.productIndustry || '',
        });
        console.log('SpeedySetup -> productDetailsModel: ', this.slickOnboardService.productDetailsModel());
      },
      error: (err) => {
        console.error('SpeedySetup -> getProductDetails error: ', err);
        this.findingProductDetails.set(false);
        this.fallbackToManualEntry();
      },
    });
  }

  /** Switch to manual entry when auto-collection can't complete, keeping any partial data. */
  private fallbackToManualEntry(): void {
    this.useManualProductEntry();
    this.speedySetupFormMessage.set(
      "We couldn't fetch everything for that link — please confirm the details below.",
    );
  }

  isValidUrl(url: string) {
    try {
      new URL(url);
      return true;
    } catch (error) {
      return false;
    }
  }

  useManualProductEntry(): void {
    this.speedySetupFormMessage.set(null);
    this.manualProductEntry.set(true);
    this.findingProductDetails.set(false);
    this.slickOnboardService.promotingModel.update((m) => ({ ...m, productLink: '' }));
    const brand = this.currentUserService.focussedBrand;
    const code = brand?.currencyCode;
    if (code) {
      this.slickOnboardService.productDetailsModel.update((d) => ({
        ...d,
        currency: d.currency?.trim() ? d.currency : code.toLowerCase(),
      }));
    }
  }

  useProductLinkEntry(): void {
    this.speedySetupFormMessage.set(null);
    this.manualProductEntry.set(false);
  }

  private productDetailsReadyForCreate(): boolean {
    const d = this.slickOnboardService.productDetailsModel();
    const priceNum = Number(String(d.productPrice ?? '').trim());
    const currencyOk = priceNum === 0 || Boolean(d.currency?.trim());
    return (
      Boolean(d.productName?.trim()) &&
      Boolean(d.productDescription?.trim()) &&
      Boolean(d.productImage?.trim()) &&
      Number.isFinite(priceNum) &&
      currencyOk
    );
  }

  onImageUploaded(event: { file: string, fileKey: string, fullUrl: string }) {
    this.slickOnboardService.productDetailsModel.set({
      ...this.slickOnboardService.productDetailsModel(),
      productImage: event.fullUrl || ''
    });
  }

  selectUgcOnly(channel: { channelName: string; shortName: string; channelId: number; channelIcon: string; }) {
    console.log('selectUgcOnly');
    this.createBriefService.selectedUgcOnly.set(!this.createBriefService.selectedUgcOnly());

    this.createBriefService.selectedPrimaryChannel.set(undefined);
    this.createBriefService.selectedVideoFormat.set(undefined);
    this.createBriefService.selectedCrossPlatformChannels.set([]);
    this.createBriefService.createShoutBrief.set(false);
  }

  selectInfluencerBrief(channel: { channelName: string; shortName: string; channelId: number; channelIcon: string; }) {
    console.log('selectInfluencerBrief', channel);
    if (this.createBriefService.selectedUgcOnly()) {
      this.createBriefService.selectedUgcOnly.set(false);
      this.createBriefService.selectedVideoFormat.set(undefined);
    }

    if (this.createBriefService.selectedPrimaryChannel() && this.createBriefService.selectedPrimaryChannel()?.channelId != channel.channelId) {
      const currentChannels = this.createBriefService.selectedCrossPlatformChannels();
      const channelIndex = currentChannels.findIndex(c => c.channelId === channel.channelId);

      if (channelIndex !== -1) {
        this.createBriefService.selectedCrossPlatformChannels.set(
          currentChannels.filter(c => c.channelId !== channel.channelId)
        );
      } else {
        // Channel doesn't exist, add it
        this.createBriefService.selectedCrossPlatformChannels.set([...currentChannels, channel]);
      }
    } else if (this.createBriefService.selectedPrimaryChannel() && this.createBriefService.selectedPrimaryChannel()?.channelId == channel.channelId) {
      if (this.createBriefService.selectedCrossPlatformChannels().length > 0) {
        this.createBriefService.selectedPrimaryChannel.set(this.createBriefService.selectedCrossPlatformChannels()[0]);
        this.createBriefService.selectedCrossPlatformChannels.set(this.createBriefService.selectedCrossPlatformChannels().slice(1));
      } else {
        this.createBriefService.selectedPrimaryChannel.set(undefined);
      }
    } else {
      this.createBriefService.selectedPrimaryChannel.set(channel);
    }

    console.log('selectedUgcOnly: ', this.createBriefService.selectedUgcOnly());
    console.log('selectedPrimaryChannel: ', this.createBriefService.selectedPrimaryChannel());
    console.log('selectedCrossPlatformChannels: ', this.createBriefService.selectedCrossPlatformChannels());
    if (this.createBriefService.selectedPrimaryChannel()) {
      this.createBriefService.createShoutBrief.set(true);
      this.createBriefService.selectedVideoFormat.set({
        id: 0,
        name: '9:16',
        description: 'Vertical',
        color: 'bg-green-500/10 text-green-400 rounded-l-md border-t border-l border-b border-green-400'
      })
    }
    console.log('--------------------------------');
  }

  isChannelSelected(channel: { channelName: string; shortName: string; channelId: number; channelIcon: string; }) {
    if (this.createBriefService.selectedPrimaryChannel() && this.createBriefService.selectedPrimaryChannel()?.channelId == channel.channelId) {
      return true;
    }

    if (this.createBriefService.selectedCrossPlatformChannels().find((c) => c.channelId == channel.channelId)) {
      return true;
    }

    return false;
  }

  /**
   * Focus (and scroll to) the first invalid field in the given ordered list.
   * Deferred so the DOM reflects the just-applied touched/invalid styling.
   */
  private focusFirstInvalid(candidates: { id: string; invalid: boolean }[]): void {
    const target = candidates.find((c) => c.invalid);
    if (!target) {
      return;
    }
    setTimeout(() => {
      const root = this.host.nativeElement as HTMLElement;
      const el = root.querySelector(`#${target.id}`) as HTMLElement | null;
      el?.focus();
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }

  submitSpeedySetup() {
    console.log('1. submitSpeedySetup');
    this.speedySetupFormMessage.set(null);

    if (this.slickOnboardService.promotingForm().invalid()) {
      this.speedySetupFormMessage.set('Whoops! Looks like something is missing.');
      const briefTitle = this.slickOnboardService.promotingForm.briefTitle();
      briefTitle.markAsTouched();
      this.focusFirstInvalid([{ id: 'brief-title', invalid: briefTitle.invalid() }]);
      return;
    }

    // "My Brand": use the pre-existing default product; no form validation or product creation.
    if (this.productChoice() === 'myBrand') {
      const product = this.brandProduct();
      if (!product) {
        this.speedySetupFormMessage.set(
          'We could not load your brand product. Please try again or choose Specific Product.',
        );
        return;
      }
      this.simulateChat();
      this.slickOnboardService.newlyCreatedProduct.set(product);
      this.slickOnboardService.startCreatorPeronsa();
      this.createQuickBrief(product);
      return;
    }

    if (this.manualProductEntry()) {
      if (this.slickOnboardService.productDetailsForm().invalid() || !this.productDetailsReadyForCreate()) {
        this.speedySetupFormMessage.set('Whoops! Looks like something is missing.');
        const pf = this.slickOnboardService.productDetailsForm;
        pf.productName().markAsTouched();
        pf.productDescription().markAsTouched();
        pf.productPrice().markAsTouched();
        pf.currency().markAsTouched();
        pf.productImage().markAsTouched();
        this.focusFirstInvalid([
          { id: 'manual-product-name', invalid: pf.productName().invalid() },
          { id: 'manual-product-description', invalid: pf.productDescription().invalid() },
          { id: 'manual-product-price', invalid: pf.productPrice().invalid() },
          { id: 'manual-product-currency', invalid: pf.currency().invalid() },
        ]);
        return;
      }
    } else {
      const link = (this.slickOnboardService.promotingForm.productLink().value() ?? '').trim();
      if (!link) {
        this.speedySetupFormMessage.set(
          'Paste a product link so we can load the details, or choose "Enter details manually" if you do not have a link.',
        );
        return;
      }
      if (!this.productDetailsReadyForCreate()) {
        this.speedySetupFormMessage.set(
          'We still need complete product details. Check the link or switch to manual entry.',
        );
        return;
      }
    }

    this.simulateChat();
    this.createProduct();
  }

  simulateChat() {
    // Stop any existing chat simulation
    this.stopChatSimulation();

    // Initialize chat messages with progress updates
    const messages = [
      "Thanks for your input! Let me think about this...",
      "Analyzing your product and requirements...",
      "Crafting the perfect brief for you...",
      "Taking everything into consideration, Brand, Product, Goals...",
      "Almost there, just applying the finishing touches..."
    ];

    // Initialize chat messages state
    this.chatMessages.set(messages.map(text => ({ text, displayedText: '', isComplete: false })));
    this.currentMessageIndex.set(0);
    this.currentCharIndex.set(0);

    // Start typing the first message
    this.typeNextMessage();
  }

  typeNextMessage() {
    const messages = this.chatMessages();
    const currentIndex = this.currentMessageIndex();

    if (currentIndex >= messages.length) {
      return;
    }

    const currentMessage = messages[currentIndex];
    this.currentCharIndex.set(0);

    this.typewriterInterval = setInterval(() => {
      const charIndex = this.currentCharIndex();
      const message = messages[currentIndex];

      if (charIndex < message.text.length) {
        const displayedText = message.text.substring(0, charIndex + 1);
        const updatedMessages = [...messages];
        updatedMessages[currentIndex] = { ...message, displayedText };
        this.chatMessages.set(updatedMessages);
        this.currentCharIndex.set(charIndex + 1);
      } else {
        // Message complete
        const updatedMessages = [...messages];
        updatedMessages[currentIndex] = { ...message, displayedText: message.text, isComplete: true };
        this.chatMessages.set(updatedMessages);
        if (this.typewriterInterval !== null) {
          clearInterval(this.typewriterInterval);
          this.typewriterInterval = null;
        }

        // Move to next message after a delay
        if (currentIndex < messages.length - 1) {
          this.messageDelay = setTimeout(() => {
            this.currentMessageIndex.set(currentIndex + 1);
            this.typeNextMessage();
          }, 800);
        }
      }
    }, 30); // Typing speed
  }

  stopChatSimulation() {
    if (this.typewriterInterval) {
      clearInterval(this.typewriterInterval);
      this.typewriterInterval = null;
    }
    if (this.messageDelay) {
      clearTimeout(this.messageDelay);
      this.messageDelay = null;
    }
  }

  simulateBriefSubmissionChat() {
    // Stop any existing chat simulation
    this.stopChatSimulation();

    // Initialize chat messages with progress updates for brief submission
    const messages = [
      "Creating your brief...",
      "Getting everything set up for content moderation...",
      "Finding creators that seem like a reasonable match...",
      "Reviewing creator profiles and matching criteria...",
      "Almost done! Finalizing everything..."
    ];

    // Initialize chat messages state
    this.chatMessages.set(messages.map(text => ({ text, displayedText: '', isComplete: false })));
    this.currentMessageIndex.set(0);
    this.currentCharIndex.set(0);

    // Start typing the first message
    this.typeNextMessage();
  }

  async createProduct() {
    console.log('2. createProduct');
    const link = (this.slickOnboardService.promotingForm.productLink().value() ?? '').trim();
    const brandWebsite =
      typeof this.currentUserService.focussedBrand?.website === 'string'
        ? this.currentUserService.focussedBrand.website.trim()
        : '';
    const websiteForProduct = link || brandWebsite;
    const rawPrice = Number(String(this.slickOnboardService.productDetailsModel().productPrice ?? '').trim());
    const productValueCents = Number.isFinite(rawPrice) ? Math.round(rawPrice * 100) : 0;

    const newProduct: CreateProductDto = {
      productName: this.slickOnboardService.productDetailsModel().productName,
      productDescription: this.slickOnboardService.productDetailsModel().productDescription,
      productImage: this.slickOnboardService.productDetailsModel().productImage,
      productValue: productValueCents,
      currency: this.slickOnboardService.productDetailsModel().currency,
      productType: 0,
      website: websiteForProduct,
      accessInstructions: '',
      creatorId: this.firebaseService.userId!,
      businessAccountId: this.currentUserService.focussedBrand.id,
    };

    console.log('newProduct: ', newProduct);

    const saveProductResult: UgcProductResponseDto = await firstValueFrom(this.productService.createUserProduct(newProduct));
    this.slickOnboardService.newlyCreatedProduct.set(saveProductResult);
    console.log('saveProductResult: ', saveProductResult);
    console.log('newlyCreatedProduct: ', this.slickOnboardService.newlyCreatedProduct());

    this.slickOnboardService.startCreatorPeronsa();

    this.createQuickBrief(saveProductResult);
  }

  regenerateBrief() {
    console.log('regenerateBrief() -> ', this.slickOnboardService.newlyCreatedProduct());
    this.quickStartIndex ++;
    if (this.quickStartIndex >= this.quickPromptOptions.length) {
      this.quickStartIndex = 0;
    }

    this.slickOnboardService.stepsHtml.set('' );
    this.slickOnboardService.doHtml.set('');
    this.slickOnboardService.dontHtml.set('');
    this.slickOnboardService.creativeConcepts.set([]);

    // Reset and restart chat simulation
    this.stopChatSimulation();
    this.chatMessages.set([]);
    this.currentMessageIndex.set(0);
    this.currentCharIndex.set(0);
    this.simulateChat();

    this.createQuickBrief(this.slickOnboardService.newlyCreatedProduct()!);
  }

  createQuickBrief(product: UgcProductResponseDto) {
    console.log('3. createQuickBrief -> product: ', product);

    if (!product || this.queryRunning()) {
      if (!product) {
        console.log('No product...');
      }
      return;
    }

    this.speedySetupFormMessage.set(null);
    this.queryRunning.set(true);
    void this.generateBriefFromGpt(product);
  }

  /**
   * Builds a fresh system+user message pair, requests a structured brief from GPT,
   * validates the schema, and retries once with a correction prompt if needed.
   */
  private async generateBriefFromGpt(product: UgcProductResponseDto): Promise<void> {
    const creativeDirection = this.quickPromptOptions[this.quickStartIndex];
    const messages = this.buildBriefGenerationMessages(product, creativeDirection);

    try {
      let parsed = await this.requestBriefJson(messages);
      console.log('AI brief object:', parsed);

      if (!this.isValidBriefPayload(parsed)) {
        console.warn('AI brief object failed schema validation; retrying once with correction prompt.');
        const correctionMessages: GptCompletionMessage[] = [
          ...messages,
          {
            role: 'assistant',
            content: JSON.stringify(parsed ?? {}),
          },
          {
            role: 'user',
            content: `Your previous reply used the wrong JSON keys or was missing required fields.
Reply again with ONLY this exact top-level shape (no other keys):
{
  "briefText": "<h2 class=\\"text-md line-6 font-medium text-gray-900 dark:text-white mb-1\\">Overview</h2><p class=\\"text-sm text-gray-600 dark:text-gray-400 mb-4\\">...</p>",
  "dos": "<ul class=\\"list-disc list-inside text-sm text-gray-600 dark:text-gray-400 mb-4\\"><li class=\\"text-xs text-gray-900 dark:text-white mb-1\\">...</li></ul>",
  "donts": "<ul class=\\"list-disc list-inside text-sm text-gray-600 dark:text-gray-400 mb-4\\"><li class=\\"text-xs text-gray-900 dark:text-white mb-1\\">...</li></ul>",
  "creativeConcepts": [
    { "id": 0, "name": "Concept name", "description": "Short plain-text description" },
    { "id": 1, "name": "Concept name", "description": "Short plain-text description" },
    { "id": 2, "name": "Concept name", "description": "Short plain-text description" }
  ]
}
Do not include campaign_details, product_details, content_guidelines, deliverables, or any other top-level keys.`,
          },
        ];
        parsed = await this.requestBriefJson(correctionMessages);
        console.log('AI brief object (retry):', parsed);
      }

      if (!this.isValidBriefPayload(parsed)) {
        this.failBriefGeneration(SpeedySetupComponent.BRIEF_GEN_ERROR);
        return;
      }

      this.applyBriefPayload(parsed);
      this.queryRunning.set(false);
    } catch (err) {
      console.error('generateBriefFromGpt failed:', err);
      this.failBriefGeneration(SpeedySetupComponent.BRIEF_GEN_ERROR);
    }
  }

  private buildBriefGenerationMessages(
    product: UgcProductResponseDto,
    creativeDirection: string,
  ): GptCompletionMessage[] {
    const briefTitle = this.slickOnboardService.promotingForm.briefTitle().value();
    const goal = this.slickOnboardService.goalInformation();
    const dontSeed = [
      !this.createBriefService.selectedUgcOnly()
        ? `- Don't post until your content has been approved.`
        : null,
      `- No explicit language or sounds, nudity, lewd, or malicious posts.`,
      `- Do NOT mention or show any competing brands.`,
      `- No third-party content or trademarks in your content due to rights.`,
    ]
      .filter((line): line is string => Boolean(line))
      .join('\n');

    const userContent = `This is my brand name: ${this.currentUserService.focussedBrand.companyName}
This is my brand description: ${this.currentUserService.focussedBrand.about}
${briefTitle ? `This is the current brief title: ${briefTitle}` : ''}
This is the product name they are advertising: ${product.productName}
This is the product description: ${product.productDescription}
This is the goal of the campaign: ${goal?.name ?? ''}: ${goal?.description ?? ''}

This brand/product/goal information is already shown to creators — do not repeat it verbatim in briefText.

Creative direction for this brief: ${creativeDirection}

Return ONLY a JSON object with exactly these four top-level keys (no others):
{
  "briefText": "<h2 class=\\"text-md line-6 font-medium text-gray-900 dark:text-white mb-1\\">Overview</h2><p class=\\"text-sm text-gray-600 dark:text-gray-400 mb-4\\">Explain what the creator should make.</p><h2 class=\\"text-md line-6 font-medium text-gray-900 dark:text-white mb-1\\">What we're looking for</h2><p class=\\"text-sm text-gray-600 dark:text-gray-400 mb-4\\">Tone and content direction.</p><h2 class=\\"text-md line-6 font-medium text-gray-900 dark:text-white mb-1\\">Client USPs to include</h2><ul class=\\"list-disc list-inside text-sm text-gray-600 dark:text-gray-400 mb-4\\"><li class=\\"text-xs text-gray-900 dark:text-white mb-1\\">USP one</li></ul>",
  "dos": "<ul class=\\"list-disc list-inside text-sm text-gray-600 dark:text-gray-400 mb-4\\"><li class=\\"text-xs text-gray-900 dark:text-white mb-1\\">Show the product clearly on camera</li></ul>",
  "donts": "<ul class=\\"list-disc list-inside text-sm text-gray-600 dark:text-gray-400 mb-4\\"><li class=\\"text-xs text-gray-900 dark:text-white mb-1\\">Do not mention competing brands</li></ul>",
  "creativeConcepts": [
    { "id": 0, "name": "Concept one", "description": "A distinct video direction inspired by social trends." },
    { "id": 1, "name": "Concept two", "description": "A second distinct video direction." },
    { "id": 2, "name": "Concept three", "description": "A third distinct video direction." }
  ]
}

Field rules:
1. briefText — HTML string. Write a clear brief covering (a) what the product is/does with vivid adjectives and (b) how creators should make the video. Include three sections with professional wording for headings inspired by: "Overview", "What we're looking for in your content", and "Some client USP's for you to include in your content".
2. dos — HTML string of things the creator should do in the video.
3. donts — HTML string of things to avoid. Use these as inspiration (rewrite specifically for this brand/product; do not copy verbatim):
${dontSeed}
4. creativeConcepts — exactly 3 distinct concepts inspired by TikTok, Instagram, and YouTube trends. ids start at 0 and increment. name and description are plain text (not HTML).

HTML styling (Tailwind):
- h2: "text-md line-6 font-medium text-gray-900 dark:text-white mb-1"
- p: "text-sm text-gray-600 dark:text-gray-400 mb-4"
- ul: "list-disc list-inside text-sm text-gray-600 dark:text-gray-400 mb-4"
- li: "text-xs text-gray-900 dark:text-white mb-1"

Do NOT return campaign_details, product_details, content_guidelines, deliverables, or any other top-level keys.
Return only the JSON object. No markdown fences. No commentary.`;

    return [
      SpeedySetupComponent.BRIEF_SCHEMA_SYSTEM,
      { role: 'user', content: userContent },
    ];
  }

  private async requestBriefJson(
    messages: GptCompletionMessage[],
  ): Promise<Record<string, unknown> | null> {
    const res = (await firstValueFrom(
      this.chatGptService.sendCompletionMessage(messages),
    )) as { choices?: { message?: { content?: string } }[] };

    const raw = res?.choices?.[0]?.message?.content;
    if (!raw || typeof raw !== 'string') {
      return null;
    }

    try {
      return JSON.parse(raw) as Record<string, unknown>;
    } catch (err) {
      console.error('requestBriefJson JSON.parse failed:', err, raw);
      return null;
    }
  }

  private isValidBriefPayload(
    value: Record<string, unknown> | null,
  ): value is {
    briefText: string;
    dos: string;
    donts: string;
    creativeConcepts: unknown[];
  } {
    if (!value || typeof value !== 'object') {
      return false;
    }
    const briefText = value['briefText'];
    const dos = value['dos'];
    const donts = value['donts'];
    const creativeConcepts = value['creativeConcepts'];
    return (
      typeof briefText === 'string' &&
      briefText.trim().length > 0 &&
      typeof dos === 'string' &&
      dos.trim().length > 0 &&
      typeof donts === 'string' &&
      donts.trim().length > 0 &&
      Array.isArray(creativeConcepts) &&
      creativeConcepts.length > 0
    );
  }

  private normaliseCreativeConcepts(
    concepts: unknown[],
  ): { id: number; name: string; description: string }[] {
    return concepts.map((concept, index) => {
      const item =
        concept && typeof concept === 'object'
          ? (concept as Record<string, unknown>)
          : {};
      return {
        id: typeof item['id'] === 'number' ? item['id'] : index,
        name: typeof item['name'] === 'string' ? item['name'] : '',
        description: typeof item['description'] === 'string' ? item['description'] : '',
      };
    });
  }

  private applyBriefPayload(parsed: {
    briefText: string;
    dos: string;
    donts: string;
    creativeConcepts: unknown[];
  }): void {
    this.slickOnboardService.stepsHtml.set(parsed.briefText ?? '');
    this.slickOnboardService.doHtml.set(parsed.dos ?? '');
    this.slickOnboardService.dontHtml.set(parsed.donts ?? '');
    this.slickOnboardService.creativeConcepts.set(
      this.normaliseCreativeConcepts(parsed.creativeConcepts ?? []),
    );
  }

  private failBriefGeneration(message: string): void {
    this.stopChatSimulation();
    this.slickOnboardService.stepsHtml.set('');
    this.slickOnboardService.doHtml.set('');
    this.slickOnboardService.dontHtml.set('');
    this.slickOnboardService.creativeConcepts.set([]);
    this.queryRunning.set(false);
    this.speedySetupFormMessage.set(message);
  }

  async submitBrief() {
    console.log('6. submitBrief');
    this.savingBrief.set(true);

    // Start chat simulation for brief submission
    this.simulateBriefSubmissionChat();

    const pricingModels = await firstValueFrom(this.pmv2Service.getCustomPricingModels(this.currentUserService.focussedBrand.id, this.currentUserService.focussedBrand.currencyCode));
    console.log('pricingModels: ', pricingModels);
    const pricingModel = pricingModels[0];
    this.selectedPricingModel.set(pricingModel);
    // getBriefTypePricing() reads CreateBriefService.selectedPricingModel — keep both in sync
    this.createBriefService.selectedPricingModel.set(pricingModel);
    console.log('selectedPricingModel: ', this.selectedPricingModel());

    const newBrief: CreateBriefDto = {
      campaignId: '',
      productId: this.slickOnboardService.newlyCreatedProduct()?.id || '',
      title: this.slickOnboardService.promotingForm.briefTitle().value() || '',
      stepsHtml: this.slickOnboardService.stepsHtml(),
      doHtml: this.slickOnboardService.doHtml(),
      dontHtml: this.slickOnboardService.dontHtml(),
      creativeConcepts: this.slickOnboardService.creativeConcepts(),
      contentPillars: [],
      description: this.slickOnboardService.goalInformation()?.description || '',
      basePricingModelId: this.selectedPricingModel()?.id || '',
      videoFormat: VideoFormatEnum.Vertical,
      videoPointers: [],
      requiredCreators: 1,
      videosPerCreator: 1,
      businessAccountId: this.currentUserService.focussedBrand.id,
      sendNotStartedNotifications: true,
      noAutoPayment: false,
      deliveryType: DeliveryTypeEnum.none,
      demographics: {
        gender: [],
        interests: [],
        location: [],
        age: [],
        categoryId: 3,
        subCategoryId: 54,
        countryCodes: [],
        is18plus: false,
        influencerType: [InfluencerTypeEnum.Any],
        followerRanges: [],
      },
      briefPricing: {
        fifteenSecPrice: 0,
        thirtySecPrice: this.getThirtySecPrice(),
        sixtySecPrice: 0,
        facePrice: 0,
        ...this.createBriefService.getBriefTypePricing(),
        commissionPercentage: this.selectedPricingModel()?.commissionPercentage || 0,
        licenseCommissionPercentage: this.selectedPricingModel()?.licenseCommissionPercentage || 0,
        partnerContribution: 0,
        currencyCode: this.currentUserService.focussedBrand.currencyCode,
        ranks: [{ rank: 0, multiplier: 1 }],
        createdBy: this.firebaseService.userId || '',
        influencerLevels: [{ title: 'All Sizes', bonusValue: 0, commission: 0, followersTo: 100000000, followersFrom: 0 }],
      },
      status: BriefStatusEnum.Public,
      enableNoodle: true,
      humanInLoop: false,
      brief_audience: BriefAudienceType.ALL,
      brief_payment_method_id: null,
      brief_auto_config_id: null,
      is_appless_brief: false,
      creator_persona: undefined,
      brief_goal: this.slickOnboardService.goalInformation() ? { name: this.slickOnboardService.goalInformation()!.name, description: this.slickOnboardService.goalInformation()!.description } : undefined,
      complianceDocIds: undefined,
      enableAffiliateMarketing: false,
      isPublic: true,
    }

    console.log('newBrief: ', newBrief);

    if (this.createBriefService.selectedPrimaryChannel()) {
      newBrief.shoutBrief = {
        callToActionLink: undefined,
        suggestedCaption: undefined,
        suggestedHastags: undefined,
        socialPlatform: this.createBriefService.selectedPrimaryChannel()?.channelId || 0,
        resultsVersion: SubmittedPostVersionEnum.SuperSocial,
        crossPlatforms: this.createBriefService.selectedCrossPlatformChannels().map(channel => channel.channelId)
      }
    } else {
      newBrief.shoutBrief = undefined;
    } 

    const saveBriefResult: BriefResponseDto = await firstValueFrom(this.createBriefService.saveNewBrief(newBrief));
    console.log('saveBriefResult: ', saveBriefResult);

    this.centralManagementService.briefSearchFilters.set({
      ...this.centralManagementService.briefSearchFilters(),
      businessAccountId: this.currentUserService.focussedBrand.id
    });

    this.slickOnboardService.newlyCreatedBrief.set(saveBriefResult);
    console.log('newlyCreatedBrief: ', this.slickOnboardService.newlyCreatedBrief());

    // Stop chat simulation after brief is saved
    this.stopChatSimulation();

    this.gaService.trackOnboardingEvent('onboarding_quick_brief_created');

    this.savingBrief.set(false);

    this.briefCreated.emit(saveBriefResult);
    
  }

  getThirtySecPrice() {
    if (this.selectedPricingModel()) {
      return this.selectedPricingModel()?.thirtySecPrice || 0;
    }
    return 0;
  }
}
